import './SelectScope.scss';
import {Box, Tab, Tabs, Typography} from '@mui/material';
import {t} from 'i18next';
import ArrTreeView from '../../../common/arr-tree-view/ArrTreeView';
import ArrLazyTreeView from '../../../common/arr-tree-view/ArrLazyTreeView';
import ArrModal from '../../../common/arr-modal/ArrModal';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../shared/interfaces';
import {TreeNodeItem} from '../../../../api/types/production.type';
import FilterListIcon from '@mui/icons-material/FilterList';
import {ReactComponent as DocumentIcon} from '../../../../assets/images/document-icon.svg';
import {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from 'react';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {
  documentSelectionSchema,
  DocumentSelectionFormData,
} from '../../../../schemas/productions/productionSchema';
import {
  DocumentSelectionMethod,
  ToastType,
  FormMode,
  FormAction,
  ProductionStatus,
} from '../../create-new-production/CreateNewProductionUtils';

import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {
  useGetReProductionDocumentSelectionView,
  useSaveReProductionDocumentSelection,
  useGetReProductionDocumentsCount,
} from '../../../../hooks/productions/useReProductions';

// Type for child component ref
export interface StepChildRef {
  save: () => void;
  next: () => void;
}

type DocumentSelectionProps = {
  reProductionId: number;
  exportreProductionId?: number;
  isProductionSet?: boolean;
  getStatus: (showToast: boolean, status: ToastType, message?: string) => void;
  goNext?: () => void;
  goBack?: () => void;
  mode?: FormMode;
  onStateChange?: (state: {
    saveDisabled: boolean;
    nextDisabled: boolean;
  }) => void;
  tagTreeData?: TreeViewBaseItem<ExtendedTreeItemProps>[];
  skipToDeliveryOptions?: () => void;
  productionStatusCode?: string;
  isUpdateNeeded?: boolean;
  onUpdateNeededChange?: (value: boolean) => void;
  securityGroupId?: number;
  onFieldsChanged?: (changed: boolean) => void;
  productionSetId?: number;
};

type TreeItem = TreeViewBaseItem<ExtendedTreeItemProps>;

const transformTreeNodeToTreeView = (
  nodes: TreeNodeItem[]
): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
  if (!nodes || nodes.length === 0) return [];

  const nodeMap = new Map<string, TreeViewBaseItem<ExtendedTreeItemProps>>();
  const rootNodes: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    nodeMap.set(node.id, {
      id: node.id,
      label: node.text,
      fileType: node.isGroup ? 'group' : 'item',
      // Preserve isGroup so ArrTreeView's native group-block check fires,
      // and force disabled at the source so no downstream transform
      // (e.g. onNodeExpand) can resurrect an enabled state.
      isGroup: !!node.isGroup,
      disabled: !!node.isGroup,
      count: '0',
      children: [],
    });
  }

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const treeNode = nodeMap.get(node.id);
    if (!treeNode) continue;

    const isRoot =
      !node.parent || node.parent === '#' || !nodeMap.has(node.parent);

    if (isRoot) {
      rootNodes.push(treeNode);
    } else {
      const parent = nodeMap.get(node.parent);
      if (parent?.children) {
        parent.children.push(treeNode);
      } else if (parent) {
        parent.children = [treeNode];
      }
    }
  }

  return rootNodes;
};

const SelectScope = forwardRef<StepChildRef, DocumentSelectionProps>(
  (
    {
      reProductionId,

      getStatus,
      goNext,
      mode = FormMode.CREATE,
      onStateChange,
      tagTreeData = [],
      skipToDeliveryOptions,
      productionStatusCode = null,
      isUpdateNeeded = false,
      onUpdateNeededChange,
      securityGroupId,
      onFieldsChanged,
      productionSetId,
    },
    ref
  ) => {
    const isInProgressOrCompleted =
      productionStatusCode === ProductionStatus.COMPLETED ||
      productionStatusCode == ProductionStatus.INPROGRESS;

    const selectedSecurityGroupId = securityGroupId;

    const [isSaved, setIsSaved] = useState<boolean>(false);
    const [searchInput, setSearchInput] = useState<string>('');

    const apiParams = useMemo(
      () => ({
        securityGroupId: selectedSecurityGroupId as number,
        reProductionId: reProductionId,
      }),
      [selectedSecurityGroupId, reProductionId]
    );
    const isApiEnabled = useMemo(
      () => selectedSecurityGroupId > 0 && reProductionId > 0,
      [selectedSecurityGroupId, reProductionId]
    );

    const {
      data: documentSelectionData,
      isLoading: isDocumentSelectionLoading,
      refetch: refetchDocumentSelection,
    } = useGetReProductionDocumentSelectionView(apiParams, isApiEnabled);

    const {
      mutate: saveDocumentsSelection,
      isPending: isSavingDocumentsSelection,
    } = useSaveReProductionDocumentSelection(
      (response) => {
        const action = pendingActionRef.current;

        refetchDocumentSelection();
        getStatus(
          true,
          ToastType.Success,
          action === FormAction.NEXT
            ? t('reProduction.toastMessages.nextSuccess')
            : t('reProduction.toastMessages.saveSuccess')
        );

        const isNext = action === FormAction.NEXT;

        setIsSaved(true);
        onUpdateNeededChange?.(true);
        shouldRehydrateAfterSaveRef.current = true;

        const isTags = selectionMethod === DocumentSelectionMethod.TAGS;
        const isSearches = selectionMethod === DocumentSelectionMethod.SEARCHES;
        const isFolders = selectionMethod === DocumentSelectionMethod.FOLDERS;

        const tags = isTags ? [...selectedTags] : [];
        const searches = isSearches ? [...selectedSearches] : [];
        const folders = isFolders ? [...selectedFolders] : [];

        const currentSnapshot = {tags, searches, folders};

        setLastCountSnapshot(currentSnapshot);
        setInitialSelectionSnapshot(currentSnapshot);
        setHasUpdateBeenClicked(true);

        reset({
          action: FormAction.SAVE,
          selectionMethod,
          selectedTags: tags,
          selectedSearches: searches,
          selectedFolders: folders,
          includeFamilies,
        });

        if (isNext) {
          setTimeout(() => {
            setSelectedTags([]);
            setSelectedSearches([]);
            setSelectedFolders([]);

            lastInitRef.current = null;
            hasUserInteractedRef.current = false;
            hasInitialSnapshotSetRef.current = false;
            shouldRehydrateAfterSaveRef.current = false;

            goNext?.();
            pendingActionRef.current = null;
          }, 0);
        } else {
          pendingActionRef.current = null;
        }
      },
      (error) => {
        getStatus(
          true,
          ToastType.Error,
          (error as Error).message ||
            t(
              'productionsAndExports.createNewProduction.documentSelection.validationError'
            )
        );
        pendingActionRef.current = null;
      }
    );

    const {mutate: fetchReProductionDocumentsCount} =
      useGetReProductionDocumentsCount(
        (data) => {
          hasLiveDocCountRef.current = true;
          setTotalDocs(data?.totalDocs ?? 0);
          setDocsWithoutBates(data?.docsWithoutBates ?? 0);
          setDocsInMultipleProductions(data?.docsInMultipleProductions ?? 0);
          setIsLoadingDocCount(false);
        },
        () => {
          setIsLoadingDocCount(false);
        }
      );

    const pendingActionRef = useRef<FormAction | null>(null);
    const [selectionMethod, setSelectionMethod] =
      useState<DocumentSelectionMethod>(DocumentSelectionMethod.SEARCHES);
    const hasLiveDocCountRef = useRef(false);
    const getContextType = useCallback(():
      | 'TAGS'
      | 'FOLDERS'
      | 'SAVEDSEARCH' => {
      if (selectionMethod === DocumentSelectionMethod.FOLDERS) return 'FOLDERS';
      if (selectionMethod === DocumentSelectionMethod.TAGS) return 'TAGS';
      return 'SAVEDSEARCH';
    }, [selectionMethod]);

    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [selectedSearches, setSelectedSearches] = useState<string[]>([]);
    const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
    const [indeterminateSearches, setIndeterminateSearches] = useState<
      string[]
    >([]);
    const [totalDocs, setTotalDocs] = useState<number>(0);
    const [docsWithoutBates, setDocsWithoutBates] = useState<number>(0);
    const [docsInMultipleProductions, setDocsInMultipleProductions] =
      useState<number>(0);

    const [familyDocs, setFamilyDocs] = useState<number>(0);
    const formatCount = (count: number) => count.toLocaleString('en-US');
    const [includeFamilies, setIncludeFamilies] = useState<boolean>(false);
    const [isLoadingDocCount, setIsLoadingDocCount] = useState<boolean>(false);
    const [showExportWarningModal, setShowExportWarningModal] =
      useState<boolean>(false);
    const [componentIsDirty, setComponentIsDirty] = useState<boolean>(false);
    const [hasUpdateBeenClicked, setHasUpdateBeenClicked] =
      useState<boolean>(false);

    const [lastCountSnapshot, setLastCountSnapshot] = useState<{
      tags: string[];
      searches: string[];
      folders: string[];
    }>({tags: [], searches: [], folders: []});

    const [initialSelectionSnapshot, setInitialSelectionSnapshot] = useState<{
      tags: string[];
      searches: string[];
      folders: string[];
    }>({tags: [], searches: [], folders: []});

    const fullTreeRef = useRef<TreeViewBaseItem<ExtendedTreeItemProps>[]>([]);
    const displayedLazyItemsCacheRef = useRef<{
      source: TreeViewBaseItem<ExtendedTreeItemProps>[];
      disabled: boolean;
      result: TreeViewBaseItem<ExtendedTreeItemProps>[];
    }>({source: [], disabled: false, result: []});
    // ─── Refs to avoid stale closures in handleTreeSelectionChange ───────────
    const selectedTagsRef = useRef<string[]>(selectedTags);
    const selectedSearchesRef = useRef<string[]>(selectedSearches);
    const selectedFoldersRef = useRef<string[]>(selectedFolders);
    const selectionMethodRef = useRef<DocumentSelectionMethod>(selectionMethod);

    useEffect(() => {
      selectedSearchesRef.current = selectedSearches;
    }, [selectedSearches]);
    useEffect(() => {
      selectedFoldersRef.current = selectedFolders;
    }, [selectedFolders]);
    useEffect(() => {
      selectionMethodRef.current = selectionMethod;
    }, [selectionMethod]);
    useEffect(() => {
      selectedTagsRef.current = selectedTags;
    }, [selectedTags]);
    useEffect(() => {
      selectedSearchesRef.current = selectedSearches;
    }, [selectedSearches]);
    useEffect(() => {
      selectedFoldersRef.current = selectedFolders;
    }, [selectedFolders]);
    useEffect(() => {
      selectionMethodRef.current = selectionMethod;
    }, [selectionMethod]);
    // ─────────────────────────────────────────────────────────────────────────

    const countNodes = (nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
      let count = 0;
      const walk = (arr: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
        for (const n of arr) {
          count += 1;
          if (Array.isArray(n.children) && n.children.length) walk(n.children);
        }
      };
      walk(nodes);
      return count;
    };

    const findNode = (
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
      id: string
    ): TreeViewBaseItem<ExtendedTreeItemProps> | null => {
      for (const n of nodes) {
        if (n.id === id) return n;
        if (n.children) {
          const f = findNode(n.children, id);
          if (f) return f;
        }
      }
      return null;
    };

    const getAllChildNodeIds = (
      node: TreeViewBaseItem<ExtendedTreeItemProps>
    ): string[] => {
      if (!node.children || node.children.length === 0) return [];
      return node.children.reduce<string[]>((acc, child) => {
        if (child.id && child.id.includes('-loading')) return acc;
        return [...acc, child.id, ...getAllChildNodeIds(child)];
      }, []);
    };

    // Get all descendant IDs (excluding the node itself) from fullTreeRef
    const getAllDescendantIds = (
      node: TreeViewBaseItem<ExtendedTreeItemProps>
    ): string[] => {
      if (!node.children || node.children.length === 0) return [];
      return node.children.reduce<string[]>((acc, child) => {
        if (child.id?.includes('-loading')) return acc;
        return [...acc, child.id, ...getAllDescendantIds(child)];
      }, []);
    };

    const treeData = useMemo(() => {
      let rawData:
        | TreeNodeItem[]
        | TreeViewBaseItem<ExtendedTreeItemProps>[]
        | null = null;

      if (!documentSelectionData) {
        if (
          selectionMethod === DocumentSelectionMethod.TAGS &&
          tagTreeData?.length
        ) {
          return tagTreeData;
        }
        return [];
      }

      switch (selectionMethod) {
        case DocumentSelectionMethod.TAGS:
          rawData = documentSelectionData.tagsList || null;
          if (!rawData && tagTreeData?.length) {
            return tagTreeData;
          }
          break;
        case DocumentSelectionMethod.SEARCHES:
          rawData = documentSelectionData.searchList || null;
          break;
        case DocumentSelectionMethod.FOLDERS:
          rawData = documentSelectionData.foldersList || null;
          break;
      }

      if (!rawData || rawData.length === 0) {
        return [];
      }

      const firstItem = rawData[0];
      if (firstItem && 'label' in firstItem && !('text' in firstItem)) {
        const treeData =
          rawData as unknown as TreeViewBaseItem<ExtendedTreeItemProps>[];
        fullTreeRef.current = treeData;
        return treeData;
      }

      try {
        const transformed = transformTreeNodeToTreeView(
          rawData as unknown as TreeNodeItem[]
        );
        fullTreeRef.current = transformed;
        return transformed;
      } catch (error) {
        console.error('Error transforming tree data:', error);
        return [];
      }
    }, [selectionMethod, documentSelectionData, tagTreeData]);

    const getAllDescendantLeaves = (
      node: TreeViewBaseItem<ExtendedTreeItemProps>
    ): string[] => {
      const result: string[] = [];
      if (node.children && node.children.length > 0) {
        for (const child of node.children) {
          if (child.id && child.id.includes('-loading')) continue;
          if (!child.children || child.children.length === 0) {
            result.push(child.id);
          } else {
            result.push(...getAllDescendantLeaves(child));
          }
        }
      }
      return result;
    };

    const getDirectChildren = (
      children: TreeViewBaseItem<ExtendedTreeItemProps>[] | undefined
    ): TreeViewBaseItem<ExtendedTreeItemProps>[] =>
      (children || []).filter((c) => c && !c.id?.includes('-loading'));

    const hasDirectChildren = (
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
      nodeId: string
    ): boolean => {
      const node = findNode(nodes, nodeId);
      const directChildren = getDirectChildren(node?.children);
      return directChildren.length > 0;
    };

    const getParentCheckboxState = (
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
      contextIDs: string[]
    ): {checked: string[]; indeterminate: string[]} => {
      const checked: string[] = [];
      const indeterminate: string[] = [];

      const checkNode = (node: TreeViewBaseItem<ExtendedTreeItemProps>) => {
        const directChildren = getDirectChildren(node.children);
        if (directChildren.length > 0) {
          let selectedCount = 0;
          const totalCount = directChildren.length;

          for (const child of directChildren) {
            const isLeaf = !child.children || child.children.length === 0;
            if (isLeaf) {
              if (contextIDs.includes(child.id)) selectedCount += 1;
            } else {
              const descendantLeaves = getAllDescendantLeaves(child);
              const hasSelectedDescendant =
                descendantLeaves.length > 0 &&
                descendantLeaves.some((leafId) => contextIDs.includes(leafId));
              const isParentItselfSelected =
                descendantLeaves.length === 0 && contextIDs.includes(child.id);
              if (hasSelectedDescendant || isParentItselfSelected)
                selectedCount += 1;
            }
          }

          if (selectedCount === totalCount && totalCount > 0) {
            checked.push(node.id);
          } else if (selectedCount > 0 && selectedCount < totalCount) {
            indeterminate.push(node.id);
          }
        }

        for (const child of getDirectChildren(node.children)) {
          checkNode(child);
        }
      };

      for (const node of nodes) {
        checkNode(node);
      }

      return {checked, indeterminate};
    };

    const findParentNodeIds = (
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
      selectedIds: string[],
      parentIds: string[] = []
    ): string[] => {
      const result: string[] = [];
      for (const node of nodes) {
        if (selectedIds.includes(node.id)) {
          result.push(...parentIds);
        }
        if (node.children && node.children.length > 0) {
          const childParentIds = [...parentIds, node.id];
          const childResults = findParentNodeIds(
            node.children,
            selectedIds,
            childParentIds
          );
          result.push(...childResults);
        }
      }
      return [...new Set(result)];
    };

    const filterSearchesSelectedItems = (
      combined: string[],
      fullTree: TreeViewBaseItem<ExtendedTreeItemProps>[],
      baseSelected: string[]
    ): string[] => {
      const state = getParentCheckboxState(fullTree, baseSelected);
      return combined.filter(
        (id) => !hasDirectChildren(fullTree, id) || state.checked.includes(id)
      );
    };

    const selectedItems = useMemo(() => {
      let baseSelected: string[] = [];
      switch (selectionMethod) {
        case DocumentSelectionMethod.TAGS:
          baseSelected = Array.isArray(selectedTags) ? selectedTags : [];
          break;
        case DocumentSelectionMethod.SEARCHES:
          baseSelected = Array.isArray(selectedSearches)
            ? selectedSearches
            : [];
          break;
        case DocumentSelectionMethod.FOLDERS:
          baseSelected = Array.isArray(selectedFolders) ? selectedFolders : [];
          break;
        default:
          baseSelected = [];
      }

      if (!Array.isArray(baseSelected)) {
        baseSelected = [];
      }

      const isLargeData = countNodes(fullTreeRef.current) > 2000;
      if (isLargeData && baseSelected.length > 0 && treeData.length > 0) {
        const parentNodeIds = findParentNodeIds(
          fullTreeRef.current,
          baseSelected
        );
        const combined = [...new Set([...baseSelected, ...parentNodeIds])];

        if (
          selectionMethod === DocumentSelectionMethod.SEARCHES &&
          fullTreeRef.current.length > 0
        ) {
          return filterSearchesSelectedItems(
            combined,
            fullTreeRef.current,
            baseSelected
          );
        }

        return combined;
      }

      return baseSelected;
    }, [
      selectionMethod,
      selectedTags,
      selectedSearches,
      selectedFolders,
      treeData,
    ]);

    const indeterminateNodeIdsForTree = useMemo(() => {
      if (selectionMethod !== DocumentSelectionMethod.SEARCHES) return [];
      if (!fullTreeRef.current?.length) return indeterminateSearches;
      const baseSelected = Array.isArray(selectedSearches)
        ? selectedSearches
        : [];
      if (baseSelected.length === 0) return [];
      const state = getParentCheckboxState(fullTreeRef.current, baseSelected);
      return state.indeterminate;
    }, [selectionMethod, selectedSearches, indeterminateSearches]);

    useEffect(() => {
      let ids: string[] = [];

      if (selectionMethod === DocumentSelectionMethod.TAGS) {
        ids = getLeafOnlySelection(selectedTags);
      } else if (selectionMethod === DocumentSelectionMethod.FOLDERS) {
        ids = getLeafOnlySelection(selectedFolders);
      } else {
        ids = getLeafOnlySelection(selectedSearches);
      }

      if (!ids.length || !selectedSecurityGroupId) {
        return;
      }

      setIsLoadingDocCount(true);

      fetchReProductionDocumentsCount({
        isMarkComplete: false,
        reProductionSetId: productionSetId,
        securityGroupId: selectedSecurityGroupId,
        reProductionDocumentsSelectionRequest: {
          contextType: getContextType(),
          contextIDs: ids.map(Number),
        },
      });
    }, [
      selectionMethod,
      selectedTags,
      selectedSearches,
      selectedFolders,
      selectedSecurityGroupId,
    ]);

    useEffect(() => {
      const selection = documentSelectionData?.productionDocumentsSelection;
      if (
        !selection ||
        !selection.contextIDs ||
        selection.contextIDs.length === 0
      )
        return;
      if (isUpdatingDocCountRef.current) return;
      if (isUserClearedRef.current) return;
      if (hasUserInteractedRef.current) return;

      const contextType = selection.contextType || 0;
      const ids = selection.contextIDs.map(String);

      const shouldInitializeSearches =
        (contextType === 3 || selection.isSavedSearchSelected) &&
        selectedSearches.length === 0 &&
        ids.length > 0 &&
        selectionMethod === DocumentSelectionMethod.SEARCHES;

      const shouldInitializeTags =
        (contextType === 2 || selection.isTagsSelected) &&
        selectedTags.length === 0 &&
        ids.length > 0 &&
        selectionMethod === DocumentSelectionMethod.TAGS;

      const shouldInitializeFolders =
        (contextType === 1 || selection.isFoldersSelected) &&
        selectedFolders.length === 0 &&
        ids.length > 0 &&
        selectionMethod === DocumentSelectionMethod.FOLDERS;

      const isLargeData = countNodes(fullTreeRef.current) > 2000;

      if (shouldInitializeSearches && treeData.length > 0) {
        let finalIds = [...ids];
        if (fullTreeRef.current.length > 0) {
          const state = getParentCheckboxState(fullTreeRef.current, ids);
          if (isLargeData && ids.length > 0) {
            finalIds = [...new Set([...ids, ...state.checked])];
          }
          setIndeterminateSearches(state.indeterminate);
        } else {
          setIndeterminateSearches([]);
        }
        setSelectedSearches(finalIds);
        setValue('selectedSearches', finalIds);
        if (!hasInitialSnapshotSetRef.current) {
          setInitialSelectionSnapshot({
            tags: [],
            searches: finalIds,
            folders: [],
          });
          hasInitialSnapshotSetRef.current = true;
        }
      } else if (shouldInitializeTags && treeData.length > 0) {
        let finalIds = [...ids];
        if (isLargeData && fullTreeRef.current.length > 0 && ids.length > 0) {
          const parentNodeIds = findParentNodeIds(fullTreeRef.current, ids);
          finalIds = [...new Set([...ids, ...parentNodeIds])];
        }
        setSelectedTags(finalIds);
        setValue('selectedTags', finalIds);
        if (!hasInitialSnapshotSetRef.current) {
          setInitialSelectionSnapshot({
            tags: finalIds,
            searches: [],
            folders: [],
          });
          hasInitialSnapshotSetRef.current = true;
        }
      } else if (shouldInitializeFolders && treeData.length > 0) {
        let finalIds = [...ids];
        if (isLargeData && fullTreeRef.current.length > 0 && ids.length > 0) {
          const parentNodeIds = findParentNodeIds(fullTreeRef.current, ids);
          finalIds = [...new Set([...ids, ...parentNodeIds])];
        }
        setSelectedFolders(finalIds);
        setValue('selectedFolders', finalIds);
        if (!hasInitialSnapshotSetRef.current) {
          setInitialSelectionSnapshot({
            tags: [],
            searches: [],
            folders: finalIds,
          });
          hasInitialSnapshotSetRef.current = true;
        }
      }
    }, [treeData, documentSelectionData, selectionMethod]);

    const allExpandedIds = useMemo(() => {
      const ids: string[] = [];
      const traverse = (nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
        for (const node of nodes) {
          ids.push(node.id);
          if (node.children?.length) {
            traverse(node.children);
          }
        }
      };
      if (treeData.length > 0) {
        traverse(treeData);
      }
      return ids;
    }, [treeData]);

    const isLargeData = useMemo(() => {
      return countNodes(fullTreeRef.current) > 2000;
    }, [treeData]);

    const [expandedNodes, setExpandedNodes] = useState<string[]>([]);

    const [lazyTreeData, setLazyTreeData] = useState<
      TreeViewBaseItem<ExtendedTreeItemProps>[]
    >([]);

    const lazyTreeDataRef = useRef(lazyTreeData);
    const isInProgressOrCompletedRef = useRef(isInProgressOrCompleted);
    lazyTreeDataRef.current = lazyTreeData;
    isInProgressOrCompletedRef.current = isInProgressOrCompleted;
    const lazyTreeUpdatedByChildRef = useRef(false);

    useEffect(() => {
      if (isLargeData) {
        if (
          lazyTreeData.length === 0 &&
          treeData.length > 0 &&
          !lazyTreeUpdatedByChildRef.current
        ) {
          const withPlaceholders = treeData.map((item: TreeItem) => {
            const hasKids =
              Array.isArray(item.children) && item.children.length > 0;
            return {
              ...item,
              hasChildren: hasKids,
              children: hasKids
                ? ([
                    {
                      id: `${item.id}-loading`,
                      label: 'Loading...',
                      disabled: true,
                    } as TreeItem,
                  ] as TreeItem[])
                : undefined,
              isChildrenLoaded: false,
            } as TreeItem;
          });
          setLazyTreeData(withPlaceholders);
          if (expandedNodes.length === 0) {
            setExpandedNodes(treeData.map((item: TreeItem) => item.id));
          }
        }
      } else if (lazyTreeData.length) {
        setLazyTreeData([]);
        setExpandedNodes([]);
        lazyTreeUpdatedByChildRef.current = false;
      }
    }, [isLargeData, treeData]);

    useEffect(() => {
      setLazyTreeData([]);
      setExpandedNodes([]);
      lazyTreeUpdatedByChildRef.current = false;
    }, [selectionMethod]);

    const markTreeDisabled = useCallback(
      (
        nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
        disabled: boolean
      ): TreeViewBaseItem<ExtendedTreeItemProps>[] =>
        disabled
          ? nodes.map((n) => ({
              ...n,
              disabled: true,
              children: n.children
                ? markTreeDisabled(n.children, true)
                : n.children,
            }))
          : nodes,
      []
    );

    const markParentsDisabled = useCallback(
      (
        nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
      ): TreeViewBaseItem<ExtendedTreeItemProps>[] =>
        nodes.map((n) => {
          const realChildren = (n.children || []).filter(
            (c) => c && !c.id?.includes('-loading')
          );
          const isParent = realChildren.length > 0 || !!(n as any).hasChildren;
          // treat any fileType==='group' the same as a parent for disabling.
          const isGroup = (n as {fileType?: string}).fileType === 'group';
          return {
            ...n,
            disabled: isParent || isGroup || isInProgressOrCompleted,
            children: n.children ? markParentsDisabled(n.children) : n.children,
          };
        }),
      [isInProgressOrCompleted]
    );

    const handleUpdateTreeData = useCallback(
      (
        data: React.SetStateAction<TreeViewBaseItem<ExtendedTreeItemProps>[]>
      ) => {
        if (data === undefined) return;
        const next =
          typeof data === 'function'
            ? ((
                data as (
                  prev: TreeViewBaseItem<ExtendedTreeItemProps>[]
                ) => TreeViewBaseItem<ExtendedTreeItemProps>[]
              )(lazyTreeDataRef.current) ?? [])
            : data;
        const toSet = isInProgressOrCompletedRef.current
          ? markTreeDisabled(next, true)
          : next;
        lazyTreeUpdatedByChildRef.current = true;
        setLazyTreeData(toSet);
      },
      [markTreeDisabled]
    );

    const {
      handleSubmit,
      setValue,
      reset,
      formState: {isDirty, isValid},
      watch,
    } = useForm<DocumentSelectionFormData>({
      mode: 'onChange',
      resolver: zodResolver(documentSelectionSchema()),
      defaultValues: {
        action: FormAction.SAVE,
        selectionMethod: DocumentSelectionMethod.SEARCHES,
        selectedTags: [],
        selectedSearches: [],
        selectedFolders: [],
        includeFamilies: false,
      },
    });

    useEffect(() => {
      if (componentIsDirty) {
        onFieldsChanged?.(true);
      }
    }, [componentIsDirty]);

    useEffect(() => {
      if (isDirty && isSaved) {
        setIsSaved(false);
      }
    }, [isDirty, isSaved]);

    const lastInitRef = useRef<string | null>(null);
    const lastProductionIdRef = useRef<number | null>(null);
    const isUpdatingDocCountRef = useRef<boolean>(false);
    const isUserClearedRef = useRef<boolean>(false);
    const hasUserSelectedRadioRef = useRef<boolean>(false);
    const hasUserInteractedRef = useRef<boolean>(false);
    const hasInitialSnapshotSetRef = useRef<boolean>(false);
    const shouldRehydrateAfterSaveRef = useRef<boolean>(false);

    useEffect(() => {
      if (lastProductionIdRef.current !== reProductionId) {
        lastInitRef.current = null;
        lastProductionIdRef.current = reProductionId;
        isUserClearedRef.current = false;
        setHasUserSelectedRadio(false);
        hasUserSelectedRadioRef.current = false;
        hasUserInteractedRef.current = false;
        shouldRehydrateAfterSaveRef.current = false;
        setInitialSelectionSnapshot({tags: [], searches: [], folders: []});
        setLastCountSnapshot({tags: [], searches: [], folders: []});
        hasInitialSnapshotSetRef.current = false;
        setHasUpdateBeenClicked(false);
        lazyTreeUpdatedByChildRef.current = false;
        hasLiveDocCountRef.current = false;

        // Hard reset local selection state whenever the re-production id
        // changes. The next render of the rehydration effect (which keys off
        // documentSelectionData) will repopulate state from the API.
        setSelectedTags([]);
        setSelectedSearches([]);
        setSelectedFolders([]);
        setIndeterminateSearches([]);
        setTotalDocs(0);
        setFamilyDocs(0);
        setIncludeFamilies(false);
        setSelectionMethod(DocumentSelectionMethod.SEARCHES);
      }
    }, [reProductionId]);

    useEffect(() => {
      const selection = documentSelectionData?.productionDocumentsSelection;
      if (!selection) return;

      const contextType = selection.contextType || 0;
      const currentKey = `${reProductionId}-${contextType}`;

      if (shouldRehydrateAfterSaveRef.current) {
        shouldRehydrateAfterSaveRef.current = false;
        lastInitRef.current = null;
        if (selection.contextIDs && selection.contextIDs.length > 0) {
          const ids = selection.contextIDs.map(String);
          if (contextType === 2 || selection.isTagsSelected) {
            setSelectedTags(ids);
            setValue('selectedTags', ids);
          } else if (contextType === 1 || selection.isFoldersSelected) {
            setSelectedFolders(ids);
            setValue('selectedFolders', ids);
          } else if (contextType === 3 || selection.isSavedSearchSelected) {
            if (fullTreeRef.current.length > 0) {
              const state = getParentCheckboxState(fullTreeRef.current, ids);
              const finalIds = [...new Set([...ids, ...state.checked])];
              setSelectedSearches(finalIds);
              setValue('selectedSearches', finalIds);
              setIndeterminateSearches(state.indeterminate);
            } else {
              setSelectedSearches(ids);
              setValue('selectedSearches', ids);
              setIndeterminateSearches([]);
            }
          }
          if (!hasLiveDocCountRef.current) {
            setTotalDocs(selection.totalNumberOfDocsIncludingFamily || 0);
          }
        }
        return;
      }

      const isInitialLoad = lastInitRef.current === null;
      const isProductionChanged =
        lastProductionIdRef.current !== reProductionId;
      const isNewDataSource = isInitialLoad || isProductionChanged;

      if (lastInitRef.current === currentKey && !isNewDataSource) {
        return;
      }

      lastInitRef.current = currentKey;

      const hasNoSavedData =
        contextType === 0 &&
        (!selection.contextIDs ||
          selection.contextIDs.length === 0 ||
          selection.contextIDs === null);

      if (!hasLiveDocCountRef.current) {
        setTotalDocs(selection.totalNumberOfDocsIncludingFamily || 0);
      }

      if (isNewDataSource && !isUpdatingDocCountRef.current) {
        setSelectedTags([]);
        setSelectedSearches([]);
        setSelectedFolders([]);
        setIndeterminateSearches([]);
        setValue('selectedTags', []);
        setValue('selectedSearches', []);
        setValue('selectedFolders', []);

        if (hasNoSavedData) {
          setSelectionMethod(DocumentSelectionMethod.SEARCHES);
          setValue('selectionMethod', DocumentSelectionMethod.SEARCHES, {
            shouldDirty: false,
          });
          setHasUserSelectedRadio(false);
          hasUserSelectedRadioRef.current = false;
          setInitialSelectionSnapshot({tags: [], searches: [], folders: []});
          hasInitialSnapshotSetRef.current = true;
        } else {
          let initialTags: string[] = [];
          let initialSearches: string[] = [];
          let initialFolders: string[] = [];

          if (contextType === 2 || selection.isTagsSelected) {
            setSelectionMethod(DocumentSelectionMethod.TAGS);
            setValue('selectionMethod', DocumentSelectionMethod.TAGS);
            if (selection.contextIDs && selection.contextIDs.length > 0) {
              const ids = selection.contextIDs.map(String);
              setSelectedTags(ids);
              setValue('selectedTags', ids);
              initialTags = ids;
            }
          } else if (contextType === 1 || selection.isFoldersSelected) {
            setSelectionMethod(DocumentSelectionMethod.FOLDERS);
            setValue('selectionMethod', DocumentSelectionMethod.FOLDERS);
            if (selection.contextIDs && selection.contextIDs.length > 0) {
              const ids = selection.contextIDs.map(String);
              setSelectedFolders(ids);
              setValue('selectedFolders', ids);
              initialFolders = ids;
            }
          } else if (contextType === 3 || selection.isSavedSearchSelected) {
            setSelectionMethod(DocumentSelectionMethod.SEARCHES);
            setValue('selectionMethod', DocumentSelectionMethod.SEARCHES);
            if (selection.contextIDs && selection.contextIDs.length > 0) {
              const ids = selection.contextIDs.map(String);
              if (fullTreeRef.current.length > 0) {
                const state = getParentCheckboxState(fullTreeRef.current, ids);
                const finalIds = [...new Set([...ids, ...state.checked])];
                setSelectedSearches(finalIds);
                setValue('selectedSearches', finalIds);
                setIndeterminateSearches(state.indeterminate);
                initialSearches = finalIds;
              } else {
                setSelectedSearches(ids);
                setValue('selectedSearches', ids);
                setIndeterminateSearches([]);
                initialSearches = ids;
              }
            }
          } else {
            setSelectionMethod(DocumentSelectionMethod.SEARCHES);
            setValue('selectionMethod', DocumentSelectionMethod.SEARCHES);
          }

          setHasUserSelectedRadio(true);
          hasUserSelectedRadioRef.current = true;
          setInitialSelectionSnapshot({
            tags: initialTags,
            searches: initialSearches,
            folders: initialFolders,
          });
          hasInitialSnapshotSetRef.current = true;
        }
      } else {
        const hasCurrentSelections =
          (selectionMethod === DocumentSelectionMethod.TAGS &&
            selectedTags.length > 0) ||
          (selectionMethod === DocumentSelectionMethod.SEARCHES &&
            selectedSearches.length > 0) ||
          (selectionMethod === DocumentSelectionMethod.FOLDERS &&
            selectedFolders.length > 0);

        const hasApiSelections =
          selection.contextIDs && selection.contextIDs.length > 0;

        if (
          hasApiSelections &&
          !hasCurrentSelections &&
          !isUpdatingDocCountRef.current &&
          !isUserClearedRef.current &&
          !hasUserInteractedRef.current
        ) {
          const ids = selection.contextIDs.map(String);
          let initialTags: string[] = [];
          let initialSearches: string[] = [];
          let initialFolders: string[] = [];

          if (contextType === 2 || selection.isTagsSelected) {
            setSelectedTags(ids);
            setValue('selectedTags', ids);
            initialTags = ids;
          } else if (contextType === 1 || selection.isFoldersSelected) {
            setSelectedFolders(ids);
            setValue('selectedFolders', ids);
            initialFolders = ids;
          } else if (contextType === 3 || selection.isSavedSearchSelected) {
            if (fullTreeRef.current.length > 0) {
              const state = getParentCheckboxState(fullTreeRef.current, ids);
              const finalIds = [...new Set([...ids, ...state.checked])];
              setSelectedSearches(finalIds);
              setValue('selectedSearches', finalIds);
              setIndeterminateSearches(state.indeterminate);
              initialSearches = finalIds;
            } else {
              setSelectedSearches(ids);
              setValue('selectedSearches', ids);
              setIndeterminateSearches([]);
              initialSearches = ids;
            }
            if (selectionMethod !== DocumentSelectionMethod.SEARCHES) {
              setSelectionMethod(DocumentSelectionMethod.SEARCHES);
              setValue('selectionMethod', DocumentSelectionMethod.SEARCHES);
            }
          }

          if (!hasInitialSnapshotSetRef.current) {
            setInitialSelectionSnapshot({
              tags: initialTags,
              searches: initialSearches,
              folders: initialFolders,
            });
            hasInitialSnapshotSetRef.current = true;
          }
        }

        if (!hasLiveDocCountRef.current) {
          setTotalDocs(selection.totalNumberOfDocsIncludingFamily || 0);
        }
      }
    }, [documentSelectionData, reProductionId, setValue]);

    const RADIO_TO_ENUM: Record<string, DocumentSelectionMethod> = {
      rdbTags: DocumentSelectionMethod.TAGS,
      rdbSearches: DocumentSelectionMethod.SEARCHES,
      rdbFolders: DocumentSelectionMethod.FOLDERS,
    };

    const ENUM_TO_RADIO: Partial<Record<DocumentSelectionMethod, string>> = {
      [DocumentSelectionMethod.TAGS]: 'rdbTags',
      [DocumentSelectionMethod.SEARCHES]: 'rdbSearches',
      [DocumentSelectionMethod.FOLDERS]: 'rdbFolders',
    } as const;

    const [hasUserSelectedRadio, setHasUserSelectedRadio] =
      useState<boolean>(false);

    const hasNoSavedDataFromApi = useMemo(() => {
      let result: boolean;
      if (hasUserSelectedRadio) {
        result = false;
      } else if (
        selectedTags.length > 0 ||
        selectedSearches.length > 0 ||
        selectedFolders.length > 0
      ) {
        result = false;
      } else {
        const selection = documentSelectionData?.productionDocumentsSelection;
        if (!selection) {
          result = true;
        } else {
          const contextType = selection.contextType || 0;
          result =
            contextType === 0 &&
            (!selection.contextIDs ||
              selection.contextIDs.length === 0 ||
              selection.contextIDs === null);
        }
      }
      return result;
    }, [
      documentSelectionData,
      selectedTags,
      selectedSearches,
      selectedFolders,
      hasUserSelectedRadio,
    ]);

    const handleSelectionMethodChange = (value: string) => {
      setComponentIsDirty(true);
      const method = RADIO_TO_ENUM[value] ?? DocumentSelectionMethod.SEARCHES;
      setSelectionMethod(method);
      setValue('selectionMethod', method, {shouldDirty: true});
      setHasUserSelectedRadio(true);
      hasUserSelectedRadioRef.current = true;
    };

    // ─── isParentNode: checks all tree sources + hasChildren flag ────────────
    // fullTreeRef may not contain lazily-expanded nodes, so we also search
    // treeData and lazyTreeData. The hasChildren flag on lazy nodes tells us
    // a node is a parent even before its children are loaded.
    const isParentNode = useCallback(
      (nodeId: string): boolean => {
        const searchTargets = [fullTreeRef.current, treeData, lazyTreeData];
        for (const tree of searchTargets) {
          const node = findNode(tree, nodeId);
          if (node) {
            const realChildren = (node.children || []).filter(
              (c) => c && !c.id?.includes('-loading')
            );
            const isGroup = (node as {fileType?: string}).fileType === 'group';
            // A node is a parent if it has real children, the lazy tree
            // flagged it with hasChildren (set before children are loaded),
            // or it is a group node (e.g. root-level groups with parent "#"
            // that may have no children loaded yet).
            return (
              realChildren.length > 0 || !!(node as any).hasChildren || isGroup
            );
          }
        }
        // Node not found in any tree — treat as parent to be safe so unknown
        // nodes never activate the summary panel or buttons.
        return true;
      },
      [treeData, lazyTreeData]
    );
    // ─────────────────────────────────────────────────────────────────────────

    const getLeafOnlySelection = useCallback(
      (ids: string[]) => ids.filter((id) => !isParentNode(id)),
      [isParentNode]
    );

    // ─── Single leaf-only selection handler ──────────────────────────────────
    // • Strip ALL parent nodes from the incoming array first.
    // • If nothing leaf-level remains, return early WITHOUT touching state —
    //   this is the key fix: a parent click must not clear an existing leaf
    //   selection or activate the summary panel.
    // • Otherwise select only the single last-clicked leaf.
    const handleTreeSelectionChange = useCallback(
      (selectedIds: string[] | null) => {
        hasUserInteractedRef.current = true;

        const method = selectionMethodRef.current;

        const applySelection = (ids: string[]) => {
          switch (method) {
            case DocumentSelectionMethod.TAGS:
              setSelectedTags(ids);
              setValue('selectedTags', ids, {shouldDirty: true});
              break;
            case DocumentSelectionMethod.SEARCHES:
              setSelectedSearches(ids);
              setIndeterminateSearches([]);
              setValue('selectedSearches', ids, {shouldDirty: true});
              break;
            case DocumentSelectionMethod.FOLDERS:
              setSelectedFolders(ids);
              setValue('selectedFolders', ids, {shouldDirty: true});
              break;
          }
        };

        // Empty click — clear selection
        if (!selectedIds || selectedIds.length === 0) {
          applySelection([]);
          return;
        }

        // Strip every parent node from the incoming array.
        // isParentNode now checks fullTreeRef + treeData + lazyTreeData
        // and also honours the hasChildren flag on lazy nodes.
        const leafOnlyIncoming = selectedIds.filter((id) => !isParentNode(id));

        // Nothing leaf-level was clicked — do NOT update state at all.
        // Leaving state untouched means the buttons and summary panel stay
        // exactly as they were before the parent checkbox was clicked.
        if (leafOnlyIncoming.length === 0) {
          return;
        }

        const getCurrentSelection = (): string[] => {
          switch (method) {
            case DocumentSelectionMethod.TAGS:
              return selectedTagsRef.current;
            case DocumentSelectionMethod.SEARCHES:
              return selectedSearchesRef.current;
            case DocumentSelectionMethod.FOLDERS:
              return selectedFoldersRef.current;
            default:
              return [];
          }
        };

        const lastSelectedId = leafOnlyIncoming[leafOnlyIncoming.length - 1];
        const currentSelection = getCurrentSelection();

        // Clicking the already-selected leaf → deselect
        if (
          currentSelection.length === 1 &&
          currentSelection[0] === lastSelectedId
        ) {
          applySelection([]);
          return;
        }

        // Select only the single clicked leaf
        applySelection([lastSelectedId]);
      },
      [isParentNode, setValue]
    );
    // ─────────────────────────────────────────────────────────────────────────

    const initialLazyTreeData = useMemo(() => {
      return treeData.map((item: TreeItem) => {
        const hasKids =
          Array.isArray(item.children) && item.children.length > 0;
        return {
          ...item,
          hasChildren: hasKids,
          children: hasKids
            ? [{id: `${item.id}-loading`, label: 'Loading...', disabled: true}]
            : undefined,
          isChildrenLoaded: false,
          disabled: isInProgressOrCompleted,
        } as TreeItem;
      });
    }, [treeData, isInProgressOrCompleted]);

    const displayedTreeData = useMemo(() => {
      return markParentsDisabled(treeData);
    }, [treeData, markParentsDisabled]);

    const displayedLazyItems = useMemo(() => {
      const source = lazyTreeData.length ? lazyTreeData : initialLazyTreeData;
      const withParentsDisabled = markParentsDisabled(source);
      if (!isInProgressOrCompleted) return withParentsDisabled;

      const cache = displayedLazyItemsCacheRef.current;
      if (
        cache.source === source &&
        cache.disabled === isInProgressOrCompleted
      ) {
        return cache.result;
      }
      const result = markTreeDisabled(withParentsDisabled, true);
      displayedLazyItemsCacheRef.current = {
        source,
        disabled: isInProgressOrCompleted,
        result,
      };
      return result;
    }, [
      lazyTreeData,
      initialLazyTreeData,
      isInProgressOrCompleted,
      markTreeDisabled,
      markParentsDisabled,
    ]);

    const singleSelectedItems = useMemo(() => {
      switch (selectionMethod) {
        case DocumentSelectionMethod.TAGS:
          return selectedTags;
        case DocumentSelectionMethod.SEARCHES:
          return selectedSearches;
        case DocumentSelectionMethod.FOLDERS:
          return selectedFolders;
        default:
          return [];
      }
    }, [selectionMethod, selectedTags, selectedSearches, selectedFolders]);

    const leafOnlySelection = useMemo(
      () => getLeafOnlySelection(singleSelectedItems),
      [singleSelectedItems, getLeafOnlySelection]
    );

    const hasAnySelection = leafOnlySelection.length > 0;

    const isSaveDisabled =
      isInProgressOrCompleted ||
      !hasAnySelection ||
      mode === FormMode.VIEW ||
      isSavingDocumentsSelection ||
      isDocumentSelectionLoading ||
      (!isDirty && isSaved);

    const isNextDisabled =
      !hasAnySelection ||
      isSavingDocumentsSelection ||
      isDocumentSelectionLoading;

    useImperativeHandle(ref, () => ({
      save: () => {
        setValue('action', FormAction.SAVE);

        handleSubmit((data) => {
          onSubmit({...data, action: FormAction.SAVE});
        }, onInvalid)();
      },

      next: () => {
        if (isInProgressOrCompleted) {
          goNext?.();
          return;
        }
        setValue('action', FormAction.NEXT);

        handleSubmit((data) => {
          onSubmit({...data, action: FormAction.NEXT});
        }, onInvalid)();
      },
    }));

    const filteredLazyItems = useMemo(() => {
      if (!searchInput?.trim()) {
        if (displayedLazyItems.length > 0) return displayedLazyItems;
        return isInProgressOrCompleted
          ? markTreeDisabled(treeData, true)
          : treeData;
      }

      const filterNodes = (
        nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
      ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
        return nodes
          .map((n) => {
            const realChildren = (n.children || []).filter(
              (c) => c && !c.id?.includes('-loading')
            );
            const filteredChildren = filterNodes(realChildren);
            const matches = (n.label as string)
              ?.toLowerCase()
              .includes(searchInput.toLowerCase());
            if (matches || filteredChildren.length > 0) {
              return {
                ...n,
                children: filteredChildren,
                hasChildren: filteredChildren.length > 0,
                isChildrenLoaded: true,
              };
            }
            return null;
          })
          .filter(Boolean) as TreeViewBaseItem<ExtendedTreeItemProps>[];
      };

      return filterNodes(fullTreeRef.current);
    }, [
      searchInput,
      displayedLazyItems,
      treeData,
      isInProgressOrCompleted,
      markTreeDisabled,
    ]);

    useEffect(() => {
      onStateChange?.({
        saveDisabled: isSaveDisabled,
        nextDisabled: isNextDisabled,
      });
    }, [isSaveDisabled, isNextDisabled, onStateChange]);

    const onSubmit = (data: DocumentSelectionFormData) => {
      const safeSelectedTags =
        selectionMethod === DocumentSelectionMethod.TAGS
          ? getLeafOnlySelection(selectedTags)
          : [];

      const safeSelectedSearches =
        selectionMethod === DocumentSelectionMethod.SEARCHES
          ? getLeafOnlySelection(selectedSearches)
          : [];

      const safeSelectedFolders =
        selectionMethod === DocumentSelectionMethod.FOLDERS
          ? getLeafOnlySelection(selectedFolders)
          : [];

      const currentSelectionMethod = selectionMethod || data.selectionMethod;

      const tagsIDs = safeSelectedTags.map(Number).filter((id) => !isNaN(id));
      const searchesIDs = safeSelectedSearches
        .map(Number)
        .filter((id) => !isNaN(id));
      const foldersIDs = safeSelectedFolders
        .map(Number)
        .filter((id) => !isNaN(id));

      const isTagsSelected = tagsIDs.length > 0;
      const isSavedSearchSelected = searchesIDs.length > 0;
      const isFoldersSelected = foldersIDs.length > 0;

      let contextType = 'SAVEDSEARCH';
      let contextIDs: number[] = [];

      if (
        isTagsSelected &&
        currentSelectionMethod === DocumentSelectionMethod.TAGS
      ) {
        contextType = 'TAGS';
        contextIDs = tagsIDs;
      } else if (
        isSavedSearchSelected &&
        currentSelectionMethod === DocumentSelectionMethod.SEARCHES
      ) {
        contextType = 'SAVEDSEARCH';
        contextIDs = searchesIDs;
      } else if (
        isFoldersSelected &&
        currentSelectionMethod === DocumentSelectionMethod.FOLDERS
      ) {
        contextType = 'FOLDERS';
        contextIDs = foldersIDs;
      }

      const payload = {
        productionId: reProductionId,
        isMarkComplete: data.action === FormAction.NEXT,
        securityGroupId: selectedSecurityGroupId || 0,
        productionDocumentsSelectionRequest: {
          isFoldersSelected,
          isTagsSelected,
          isSavedSearchSelected,
          contextType,
          contextIDs,
        },
      };
      pendingActionRef.current = data.action;
      saveDocumentsSelection(payload);
      onFieldsChanged?.(false);
    };

    const onInvalid = () => {
      getStatus(
        true,
        ToastType.Error,
        t(
          'productionsAndExports.createNewProduction.documentSelection.validationError'
        )
      );
    };

    return (
      <>
        <Box id="select-scope-root" className="re-production-select-scope">
          <Box
            id="select-scope-header"
            className="re-production-select-scope__header"
          >
            <Typography
              id="select-scope-header-title"
              className="re-production-select-scope__header-title"
            >
              {t('reProduction.selectScope.sectionTitle', 'Select Scope')}
            </Typography>
          </Box>

          <Box
            id="select-scope-form"
            className="new-production-form-section"
            component="form"
            onSubmit={handleSubmit(onSubmit, onInvalid)}
            role="form"
            aria-label={t(
              'productionsAndExports.createNewProduction.documentSelection.headerText'
            )}
          >
            <Box
              id="select-scope-form-inner"
              className="new-production-form document-selection"
            >
              <Box
                id="select-scope-form-body"
                className="new-production-form-body"
                role="group"
                aria-labelledby="document-selection-header"
              >
                <Box
                  id="select-scope-document-selection-section"
                  className="document-selection-section"
                >
                  <Box
                    id="select-scope-selection-content"
                    className="selection-content"
                  >
                    <Box
                      id="select-scope-selection-method-radio"
                      className="selection-method-radio"
                    >
                      <Tabs
                        id="select-scope-tabs"
                        value={
                          hasNoSavedDataFromApi
                            ? 'rdbSearches'
                            : ENUM_TO_RADIO[selectionMethod] || 'rdbSearches'
                        }
                        onChange={(_, value: string) =>
                          handleSelectionMethodChange(value)
                        }
                        aria-label="tabs"
                        className="tabs"
                      >
                        <Tab
                          id="search-tab"
                          data-testid="search-tab"
                          aria-controls="tabpanel-search-tab"
                          label={t(
                            'reProduction.selectScope.scopeOptions.search'
                          )}
                          value="rdbSearches"
                          className="tab"
                          disabled={
                            mode === FormMode.VIEW || isInProgressOrCompleted
                          }
                        />
                        <Tab
                          id="tag-tab"
                          data-testid="tag-tab"
                          aria-controls="tabpanel-tag-tab"
                          label={t('reProduction.selectScope.scopeOptions.tag')}
                          value="rdbTags"
                          className="tab"
                          disabled={
                            mode === FormMode.VIEW || isInProgressOrCompleted
                          }
                        />
                        <Tab
                          id="folder-tab"
                          data-testid="folder-tab"
                          aria-controls="tabpanel-folder-tab"
                          label={t(
                            'reProduction.selectScope.scopeOptions.folder'
                          )}
                          value="rdbFolders"
                          className="tab"
                          disabled={
                            mode === FormMode.VIEW || isInProgressOrCompleted
                          }
                        />
                      </Tabs>
                      <Box
                        id="select-scope-tags-selection-header"
                        className="tags-selection-header"
                      >
                        <ArrTextField
                          id="select-scope-search-bar"
                          className="search-textbox"
                          sizeVariant="large"
                          placeholder={t(
                            'reProduction.selectScope.searchBarPlaceholder'
                          )}
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.preventDefault();
                          }}
                          type="clear"
                          onClear={() => setSearchInput('')}
                          dataTestId={'select-scope-search-bar'}
                        />
                        {!searchInput && (
                          <FilterListIcon
                            id="select-scope-filter-icon"
                            fontSize="small"
                            className="arr-textfield-icon-color filter-icon"
                          />
                        )}
                      </Box>
                      <Box
                        id="select-scope-tags-tree-container"
                        className="tags-tree-container"
                        data-testid="select-scope-tags-tree-container"
                      >
                        {isDocumentSelectionLoading ? (
                          <Typography
                            id="select-scope-loading-text"
                            className="typo-text body2_size"
                          >
                            Loading...
                          </Typography>
                        ) : treeData && treeData.length > 0 ? (
                          isLargeData ? (
                            <ArrLazyTreeView
                              key={`ds-lazy-tree-${selectionMethod}-${reProductionId}`}
                              id={`select-scope-lazy-tree-${selectionMethod}`}
                              itemsList={filteredLazyItems}
                              updateTreeData={handleUpdateTreeData}
                              setSelectedItem={handleTreeSelectionChange}
                              treeSelectedItems={singleSelectedItems}
                              indeterminateNodeIds={
                                selectionMethod ===
                                DocumentSelectionMethod.SEARCHES
                                  ? indeterminateNodeIdsForTree
                                  : []
                              }
                              multipleSelection
                              isShowCheckbox
                              isShowCountLabel={false}
                              showActions={false}
                              expandedNodes={
                                searchInput?.trim()
                                  ? (() => {
                                      const ids: string[] = [];
                                      const collect = (
                                        nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
                                      ) => {
                                        for (const n of nodes) {
                                          ids.push(n.id);
                                          if (n.children?.length)
                                            collect(n.children);
                                        }
                                      };
                                      collect(filteredLazyItems);
                                      return ids;
                                    })()
                                  : expandedNodes
                              }
                              onExpandedNodesChange={(ids) =>
                                setExpandedNodes(ids)
                              }
                              onNodeExpand={async (nodeId: string) => {
                                if (searchInput?.trim()) return [];
                                const srcNode = findNode(
                                  fullTreeRef.current,
                                  nodeId
                                );
                                if (
                                  !srcNode ||
                                  !Array.isArray(srcNode.children)
                                )
                                  return [];
                                const actualChildren = srcNode.children.filter(
                                  (c) => c && !c.id.includes('-loading')
                                );
                                return actualChildren.map((c) => {
                                  const hasKids =
                                    Array.isArray(c.children) &&
                                    c.children.some(
                                      (gc) => gc && !gc.id.includes('-loading')
                                    );
                                  const childIsGroup =
                                    (c as {fileType?: string}).fileType ===
                                      'group' ||
                                    !!(c as {isGroup?: boolean}).isGroup;
                                  return {
                                    ...c,
                                    isGroup: childIsGroup,
                                    hasChildren: hasKids,
                                    isChildrenLoaded: !hasKids,
                                    // Group nodes are never selectable; otherwise
                                    // honour parent-with-children and the global
                                    // read-only flag.
                                    disabled:
                                      childIsGroup ||
                                      hasKids ||
                                      isInProgressOrCompleted,
                                    children: hasKids
                                      ? [
                                          {
                                            id: `${c.id}-loading`,
                                            label: 'Loading...',
                                            disabled: true,
                                          },
                                        ]
                                      : undefined,
                                  };
                                });
                              }}
                            />
                          ) : (
                            <Box
                              id={`select-scope-tree-${selectionMethod}-${reProductionId}`}
                            >
                              <ArrTreeView
                                key={`ds-tree-${selectionMethod}-${reProductionId}`}
                                itemsList={displayedTreeData}
                                setSelectedItem={handleTreeSelectionChange}
                                treeSelectedItems={singleSelectedItems}
                                multipleSelection={true}
                                isShowCheckbox={true}
                                isShowCountLabel={false}
                                defaultExpandedIds={allExpandedIds}
                                isShowFileType={false}
                                checkboxGroupSelection={false}
                                searchKeyword={searchInput || undefined}
                                expandOnSearch={true}
                              />
                            </Box>
                          )
                        ) : (
                          <Typography
                            id="select-scope-empty-text"
                            className="typo-text body2_size"
                          >
                            {selectionMethod === DocumentSelectionMethod.TAGS &&
                              t(
                                'productionsAndExports.createNewProduction.documentSelection.noTagsAvailable'
                              )}
                            {selectionMethod ===
                              DocumentSelectionMethod.SEARCHES &&
                              t(
                                'productionsAndExports.createNewProduction.documentSelection.searchesPlaceholder'
                              )}
                            {selectionMethod ===
                              DocumentSelectionMethod.FOLDERS &&
                              t(
                                'productionsAndExports.createNewProduction.documentSelection.foldersPlaceholder'
                              )}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box
                      id="select-scope-doc-count-panel"
                      className="doc-count-panel"
                    >
                      <Box
                        id="select-scope-selection-summary-panel"
                        className="selection-summary-panel"
                      >
                        {isLoadingDocCount ? (
                          <Box>
                            <ArrLoadingSpinner show={true} />
                          </Box>
                        ) : (
                          <>
                            <Box
                              id="select-scope-summary-main"
                              className="selection-summary-panel__main"
                            >
                              <Typography
                                id="select-scope-summary-count"
                                data-testid="select-scope-summary-count"
                                className="selection-summary-panel__count"
                              >
                                <DocumentIcon id="select-scope-document-icon" />
                                {hasAnySelection
                                  ? formatCount(totalDocs)
                                  : '---'}
                              </Typography>

                              <Typography
                                id="select-scope-summary-label"
                                data-testid="select-scope-summary-label"
                                className="selection-summary-panel__label"
                              >
                                {t(
                                  'reProduction.selectScope.totalDocsInSelection',
                                  'Total Docs in Selection'
                                )}
                              </Typography>
                            </Box>

                            {hasAnySelection && (
                              <>
                                <Box
                                  id="select-scope-summary-row-no-bates"
                                  data-testid="select-scope-summary-row-no-bates"
                                  className="selection-summary-panel__row"
                                >
                                  <Typography className="selection-summary-panel__warning-icon">
                                    &#9888;
                                  </Typography>
                                  <Typography className="selection-summary-panel__row-text">
                                    <Typography
                                      component="span"
                                      fontWeight="bold"
                                    >
                                      {docsWithoutBates}
                                    </Typography>{' '}
                                    {t(
                                      'reProduction.selectScope.docCounts.withoutBatesNumber'
                                    )}
                                  </Typography>
                                </Box>

                                <Box
                                  id="select-scope-summary-row-multi-prod"
                                  data-testid="select-scope-summary-row-multi-prod"
                                  className="selection-summary-panel__row"
                                >
                                  <Typography className="selection-summary-panel__warning-icon">
                                    &#9888;
                                  </Typography>
                                  <Typography className="selection-summary-panel__row-text">
                                    <Typography
                                      component="span"
                                      fontWeight="bold"
                                    >
                                      {docsInMultipleProductions}
                                    </Typography>{' '}
                                    {t(
                                      'reProduction.selectScope.docCounts.multipleProductions'
                                    )}
                                  </Typography>
                                </Box>
                              </>
                            )}
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {showExportWarningModal && (
          <ArrModal
            showModal={showExportWarningModal}
            showClose={false}
            onClose={() => setShowExportWarningModal(false)}
            title={t(
              'productionsAndExports.createNewProduction.documentSelection.exportWarningModal.title'
            )}
            description={t(
              'productionsAndExports.createNewProduction.documentSelection.exportWarningModal.description'
            )}
            buttons={[
              {
                id: 'select-scope-export-warning-ok-btn',
                label: t('common.ok'),
                onClick: () => {
                  setShowExportWarningModal(false);
                  if (isInProgressOrCompleted || !isUpdateNeeded) {
                    skipToDeliveryOptions?.();
                    return;
                  }
                  setValue('action', FormAction.NEXT);
                  handleSubmit((data) => {
                    onSubmit(data);
                  })();
                },
                variant: 'primary',
                dataTestId: 'select-scope-export-warning',
              },
            ]}
            dataTestId={'select-scope-export-warning-ok-btn'}
          />
        )}
      </>
    );
  }
);

SelectScope.displayName = 'SelectScope';

export default SelectScope;
